import React, { useEffect, useState } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Button,
  Input,
  Row,
  Col,
} from "reactstrap";
import axiosInstance from "../request";
import { URL } from "../api";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

const PayoutRequest = () => {
  const [disbursals, setDisbursals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const [selectedIds, setSelectedIds] = useState([]);
  const [actionStatus, setActionStatus] = useState("");
  const [comment, setComment] = useState("");

  const [lenders, setLenders] = useState([]);
  const [loanLenderMap, setLoanLenderMap] = useState({});
  const [assignedLenderMap, setAssignedLenderMap] = useState({});
  const [assigning, setAssigning] = useState(false);

  const userId = localStorage.getItem("userId");


  const fetchCompletedDisbursals = async () => {
    try {
      setLoading(true);
      setError(null);
      const lsCompanyCode = localStorage.getItem("companyCode");
      const companyCode =
        lsCompanyCode && lsCompanyCode !== "null" && lsCompanyCode !== ""
          ? lsCompanyCode
          : 0;
      const response = await axiosInstance.get(URL.SUCCESS_FUND, {
        params: { companyCode },
      });
      setDisbursals(response.data || []);
    } catch (err) {
      setError("Failed to load disbursals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLenders = async () => {
    try {
      const response = await axiosInstance.get(URL.GET_ALL_LENDER);
      setLenders(response.data || []);
    } catch (err) {
      toast.error("Failed to load lenders.");
    }
  };

  useEffect(() => {
    fetchCompletedDisbursals();
    fetchLenders();
  }, []);

  const handleLenderChange = (loanId, lenderId) => {
    setLoanLenderMap((prev) => ({
      ...prev,
      [loanId]: lenderId,
    }));
  };

  const handleAssignLenders = async () => {
    const entries = Object.entries(loanLenderMap).filter(
      ([, lenderId]) => lenderId
    );

    if (entries.length === 0) {
      toast.error("Please select a lender for at least one loan");
      return;
    }

    try {
      setAssigning(true);
      const responses = await Promise.all(
        entries.map(([loanId, lenderId]) =>
          axiosInstance.post(URL.ASSIGN_LENDER, null, {
            params: { loanId, lenderId },
          })
        )
      );

      setAssignedLenderMap((prev) => {
        const updated = { ...prev };
        entries.forEach(([loanId, lenderId], index) => {
          const data = responses[index]?.data;
          updated[loanId] = data?.lenderId ?? data?.lender_id ?? lenderId;
        });
        return updated;
      });

      toast.success("Lender assigned successfully");
    } catch (err) {
      toast.error("Failed to assign lender. Try again.");
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };


  const handlePerformAction = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one request");
      return;
    }
    if (!actionStatus) {
      toast.error("Please select status (Approve/Reject)");
      return;
    }
    if (!comment.trim()) {
      toast.error("Please add a comment");
      return;
    }
    if (!userId) {
      toast.error("User ID not found in local storage");
      return;
    }

    const selectedDisbursals = disbursals.filter((d) =>
      selectedIds.includes(d.id)
    );
    const missingLender = selectedDisbursals.find(
      (d) => !assignedLenderMap[d.loanId]
    );
    if (missingLender) {
      toast.error(`Please assign a lender for loan ${missingLender.loanId} first`);
      return;
    }

    try {
      setLoading(true);

      await Promise.all(
        selectedDisbursals.map((d) =>
          axiosInstance.post(URL.APPROVE_REQUEST_PAYOUT, null, {
            params: {
              disbursalId: d.id,
              newStatus: actionStatus,
              comment,
              commentBy: userId,
              userId,
              lenderId: assignedLenderMap[d.loanId],
            },
          })
        )
      );

      toast.success("Action performed successfully");
      setSelectedIds([]);
      setActionStatus("");
      setComment("");
      fetchCompletedDisbursals();
    } catch (err) {
      toast.error("Failed to perform action. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <h3 className="mb-4">Payout Requests</h3>

      {loading && (
        <div className="text-center">
          <Spinner color="primary" /> Loading...
        </div>
      )}

      {error && (
        <Alert color="danger" className="text-center">
          {error}
        </Alert>
      )}

      {/* 🔹 Action Panel */}
      <Row className="mb-3 align-items-end">
        <Col md="3">
          <label>Status</label>
          <Input
            type="select"
            value={actionStatus}
            onChange={(e) => setActionStatus(e.target.value)}
          >
            <option value="">Select Status</option>
            <option value="5">Approve (Ready for Payout)</option>
            <option value="7">Reject (Reject Payout)</option>
          </Input>
        </Col>
        <Col md="5">
          <label>Comment</label>
          <Input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter comment"
          />
        </Col>
        <Col md="2">
          <Button
            color="primary"
            onClick={handlePerformAction}
            disabled={selectedIds.length === 0}
          >
            Perform Action
          </Button>
        </Col>
        <Col md="2">
          <Button
            color="secondary"
            onClick={handleAssignLenders}
            disabled={assigning}
          >
            {assigning ? "Assigning..." : "Assign Lender"}
          </Button>
        </Col>
      </Row>

      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover responsive className="align-middle">
            <thead className="table-dark">
              <tr>
                <th>
                  <Input
                    type="checkbox"
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked ? disbursals.map((d) => d.id) : []
                      )
                    }
                    checked={
                      selectedIds.length > 0 &&
                      selectedIds.length === disbursals.length
                    }
                  />
                </th>
                <th style={{ whiteSpace: "nowrap" }}>S.No.</th>
                <th style={{ whiteSpace: "nowrap" }}>Disbursal ID</th>
                <th style={{ whiteSpace: "nowrap" }}>Cid</th>
                <th style={{ whiteSpace: "nowrap" }}>Lid</th>
                <th style={{ whiteSpace: "nowrap" }}>Lender</th>
                <th style={{ whiteSpace: "nowrap" }}>Approved Amt</th>
                <th style={{ whiteSpace: "nowrap" }}>Disbursal Amt</th>
                <th style={{ whiteSpace: "nowrap" }}>Fund Account ID</th>
                <th style={{ whiteSpace: "nowrap" }}>Payout ID</th>
                <th style={{ whiteSpace: "nowrap" }}>Disbursal UTR</th>
                <th style={{ whiteSpace: "nowrap" }}>Checker Name</th>
                <th style={{ whiteSpace: "nowrap" }}>Checker Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Disbursal Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Create Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Update Date</th>
                <th style={{ whiteSpace: "nowrap" }}>Payout Reason</th>
                <th style={{ whiteSpace: "nowrap" }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {disbursals.length > 0 ? (
                disbursals.map((d, index) => (
                  <tr key={d.id}>
                    <td>
                      <Input
                        type="checkbox"
                        checked={selectedIds.includes(d.id)}
                        onChange={() => handleCheckboxChange(d.id)}
                      />
                    </td>
                    <td>{index + 1}</td>
                    <td>{d.id}</td>
                    <td>{d.customerId}</td>
                    <td>{d.loanId}</td>
                    <td style={{ minWidth: "150px" }}>
                      <Input
                        type="select"
                        value={loanLenderMap[d.loanId] || ""}
                        onChange={(e) =>
                          handleLenderChange(d.loanId, e.target.value)
                        }
                      >
                        <option value="">Select Lender</option>
                        {lenders.map((l) => (
                          <option
                            key={l.lender_id ?? l.id}
                            value={l.lender_id ?? l.id}
                          >
                            {l.lenderName || l.lender_name || l.name}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td>{d.approvedAmt}</td>
                    <td>{d.disbursalAmt}</td>
                    <td>{d.fundAccountId}</td>
                    <td>{d.payoutId}</td>
                    <td>{d.disbursalUtr}</td>
                    <td>{d.checkerName || "-"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDate(d.checkerDate)}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDate(d.disbursalDate)}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDate(d.createDate)}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatDate(d.updateDate)}
                    </td>
                    <td>{d.failedReason || "-"}</td>
                    <td>{d.description || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="18" className="text-center">
                    No Requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <ToastContainer />
        </div>
      )}
    </Container>
  );
};

export default PayoutRequest;
